import { useState } from 'react';
import { ShieldAlert, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { AgenteIaContextoSeletor, type AgenteIaContexto } from '@/components/agentes-ia/AgenteIaContextoSeletor';
import { useAgenteIaJob } from '@/hooks/useAgenteIaJob';
import { useGuardiaoResultado } from '@/hooks/useGuardiaoDisputa';
import { useEmpresa } from '@/hooks/useEmpresa';
import { riscoBadgeVariant } from '@/lib/riskBadge';
import type { ConcorrenteAnalisado } from '@/types/agentesIa';

function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-ds-text-secondary italic text-center py-8">{message}</p>;
}

function GuardiaoDisputa() {
  const [contexto, setContexto] = useState<AgenteIaContexto | null>(null);
  const [concorrenteSelecionado, setConcorrenteSelecionado] = useState<ConcorrenteAnalisado | null>(null);

  const { data: empresa } = useEmpresa(contexto ? String(contexto.empresaId) : undefined);
  const { enqueue, isEnqueuing, jobStatus, reset } = useAgenteIaJob('guardiao-disputa', contexto?.empresaId);
  const { data: resultado, isLoading: resultadoLoading } = useGuardiaoResultado(
    contexto?.empresaId,
    contexto?.licitacaoId,
    jobStatus?.status
  );

  const isRunning = Boolean(jobStatus && jobStatus.status !== 'completed' && jobStatus.status !== 'failed');
  const semConcorrentes = Boolean(empresa && (empresa.concorrentes?.length ?? 0) === 0);

  const handleAnalisar = async () => {
    if (!contexto) return;
    reset();
    await enqueue({ licitacaoId: contexto.licitacaoId, documentoId: contexto.documentoId });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ds-text-primary flex items-center gap-2">
          <ShieldAlert className="h-6 w-6 text-ds-success" />
          Guardião da Disputa
        </h1>
        <p className="text-ds-text-secondary mt-1">
          Inteligência competitiva sobre os concorrentes na disputa.
        </p>
      </div>

      <AgenteIaContextoSeletor onContextoCompleto={setContexto} />

      <Card>
        <CardHeader>
          <CardTitle>Analisar concorrência</CardTitle>
          <CardDescription>Dispara a análise de risco e histórico dos concorrentes cadastrados.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div title={semConcorrentes ? 'Cadastre concorrentes na ficha da empresa antes de analisar' : undefined}>
            <Button
              onClick={handleAnalisar}
              disabled={!contexto || isEnqueuing || isRunning || semConcorrentes}
              isLoading={isEnqueuing || isRunning}
            >
              {isEnqueuing || isRunning ? 'Analisando concorrência...' : 'Analisar concorrência'}
            </Button>
          </div>

          {contexto && semConcorrentes && (
            <p className="text-sm text-ds-warning">
              Esta empresa não tem concorrentes cadastrados. Cadastre ao menos um na ficha da empresa para analisar.
            </p>
          )}

          {jobStatus?.status === 'failed' && (
            <div className="flex items-start gap-3 rounded-lg border border-ds-danger/20 bg-ds-danger/10 p-4">
              <AlertTriangle className="h-5 w-5 text-ds-danger shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-ds-danger">Falha ao analisar concorrência</p>
                <p className="text-sm text-ds-text-secondary mt-1">{jobStatus.failedReason}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Concorrentes analisados</CardTitle>
        </CardHeader>
        <CardContent>
          {!contexto ? (
            <EmptyState message="Selecione empresa, licitação e documento para ver a análise." />
          ) : resultadoLoading ? (
            <p className="text-center text-ds-text-secondary py-8">Carregando...</p>
          ) : !resultado ? (
            <EmptyState message="Nenhuma análise executada ainda para esta licitação." />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Risco</TableHead>
                    <TableHead>Margem estimada</TableHead>
                    <TableHead>Histórico de vitórias</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resultado.concorrentes_analisados.map((concorrente, index) => (
                    <TableRow
                      key={`${concorrente.nome}-${index}`}
                      className="cursor-pointer"
                      onClick={() => setConcorrenteSelecionado(concorrente)}
                    >
                      <TableCell>{concorrente.nome}</TableCell>
                      <TableCell>
                        <Badge variant={riscoBadgeVariant(concorrente.risco)} size="sm">{concorrente.risco}</Badge>
                      </TableCell>
                      <TableCell>{concorrente.margem_estimada ?? '-'}</TableCell>
                      <TableCell>{concorrente.historico_vitorias ?? '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {resultado.alertas.length > 0 && (
                <div className="mt-6 space-y-3">
                  <h4 className="text-sm font-semibold text-ds-text-primary">Alertas</h4>
                  {resultado.alertas.map((alerta, index) => (
                    <div
                      key={`${alerta.tipo}-${index}`}
                      className="rounded-lg border border-ds-border bg-ds-surface2 p-4"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-ds-text-primary">{alerta.tipo}</span>
                        <Badge variant={riscoBadgeVariant(alerta.severidade)} size="sm">{alerta.severidade}</Badge>
                      </div>
                      <p className="text-sm text-ds-text-secondary mt-1">{alerta.descricao}</p>
                      {alerta.acao_sugerida && (
                        <p className="text-sm text-ds-info mt-1">Ação sugerida: {alerta.acao_sugerida}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={Boolean(concorrenteSelecionado)}
        onClose={() => setConcorrenteSelecionado(null)}
        title={concorrenteSelecionado?.nome || 'Concorrente'}
      >
        {concorrenteSelecionado && (
          <div className="space-y-3 text-sm">
            <div>
              <span className="font-medium text-ds-text-secondary">Preços de referência: </span>
              <span className="text-ds-text-primary">{concorrenteSelecionado.precos_referencia ?? '-'}</span>
            </div>
            <div>
              <span className="font-medium text-ds-text-secondary">Forças: </span>
              <span className="text-ds-text-primary">{concorrenteSelecionado.forcas ?? '-'}</span>
            </div>
            <div>
              <span className="font-medium text-ds-text-secondary">Fraquezas: </span>
              <span className="text-ds-text-primary">{concorrenteSelecionado.fraquezas ?? '-'}</span>
            </div>
            <div>
              <span className="font-medium text-ds-text-secondary">Recomendação estratégica: </span>
              <span className="text-ds-text-primary">{concorrenteSelecionado.recomendacao_estrategica ?? '-'}</span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export { GuardiaoDisputa };
